import { useRef, useState } from "react";
import {
	Animated,
	FlatList,
	type LayoutChangeEvent,
	type NativeScrollEvent,
	type NativeSyntheticEvent,
	StyleSheet,
} from "react-native";
import { Text, useTheme } from "react-native-paper";
import Box from "../Box";
import { getInputRange, getLeftOutputRange, getOutputRange } from "./helpers";

type CarouselProps = {
	data: Array<{ id: string; imageUrl: string; label?: string }>;
	mode?: "hero" | "multibrowse" | "uncontained";
	height: number;
};

const Carousel = ({ data, mode = "hero", height }: CarouselProps) => {
	const flatListRef = useRef<FlatList>(null);
	const [carouselWidth, setCarouselWidth] = useState(0);
	const { current: scrollX } = useRef(new Animated.Value(0));
	const { colors, roundness } = useTheme();

	const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
		scrollX.setValue(event.nativeEvent.contentOffset.x);
	};
	const handleLayout = (event: LayoutChangeEvent) => {
		if (mode === "hero") {
			setCarouselWidth(event.nativeEvent.layout.width - 96);
		} else if (mode === "multibrowse" || mode === "uncontained") {
			setCarouselWidth(event.nativeEvent.layout.width / 2);
		} else {
			setCarouselWidth(event.nativeEvent.layout.width);
		}
	};

	const renderItem = ({
		item,
		index,
	}: {
		item: { id: string; imageUrl: string; label?: string };
		index: number;
	}) => {
		const inputRange = getInputRange(mode, carouselWidth, index);

		const scale = scrollX.interpolate({
			inputRange,
			outputRange: getOutputRange(mode, carouselWidth),
			extrapolate: "clamp",
		});

		return (
			<Box
				style={{
					width: carouselWidth,
					height,
				}}
			>
				<Animated.Image
					source={{ uri: item.imageUrl }}
					style={[
						styles.image,
						{
							width: mode === "uncontained" ? carouselWidth : scale,
							height: height,
							borderRadius: roundness * 7,
							position: "absolute",
							left: scrollX.interpolate({
								inputRange,
								outputRange: getLeftOutputRange(mode, carouselWidth),
								extrapolate: "clamp",
							}),
						},
					]}
					resizeMode="cover"
				/>
				{item.label && (
					<Text
						variant="titleMedium"
						style={[styles.label, { color: colors.onPrimary }]}
					>
						{item.label}
					</Text>
				)}
			</Box>
		);
	};

	return (
		<Box
			style={{
				width: "100%",
				height: "100%",
				paddingHorizontal: 16,
			}}
			onLayout={handleLayout}
		>
			<FlatList
				ref={flatListRef}
				style={{
					width: "100%",
				}}
				data={data}
				renderItem={renderItem}
				keyExtractor={(item) => item.id}
				horizontal
				snapToStart
				pagingEnabled
				onScroll={onScroll}
				showsHorizontalScrollIndicator={false}
				scrollEventThrottle={16}
				contentContainerStyle={{
					paddingVertical: 8,
					paddingRight: carouselWidth - 32,
					gap: 8,
					alignItems: "flex-start",
				}}
			/>
		</Box>
	);
};

const styles = StyleSheet.create({
	itemContainer: {
		justifyContent: "center",
		alignItems: "center",
		padding: 20,
	},
	image: {
		resizeMode: "cover",
	},
	label: {
		position: "absolute",
		bottom: 16,
		left: 16,
		textAlign: "center",
	},
});

export { Carousel };
